import { IntlShape } from 'react-intl';
import { EventsGetPayloadData, EventsListPayloadData } from '../data-types/events/eventsInterfaces';
import { DEFAULT_ERROR_MESSAGE } from '../data-types/initialisation/initialisationConstants';
import { CommandResponseError } from '../data-types/initialisation/initialisationInterfaces';
import { AcceptedCountries } from '../data-types/international/internationalEnums';
import { SubSubNavItem } from '../data-types/main-menu/mainMenuInterfaces';
import { sportCategories, sportIdsLegacyToNew } from '../data-types/sports/sportsConstants';
import { HORSE_RACE_LIST_ID } from '../data-types/sports/sportsEnums';
import {
  DropdownMarket,
  DropdownMarketsResponse,
  EventGetResponse,
  EventItem,
  EventListResponse,
  EventSearchResponse,
  EventStreamGetResponse,
  MarketOfferGroupResponse,
  MarketOutcomesResponse,
  MeetingsResponse,
  Race,
  RaceDetails,
  RaceGetResponse,
  RaceListResponse,
  RacingCompetitionResponse,
  RetrieveEventRequest,
  RetrieveEventsResponse,
  SportsTicketResponse,
  SubscriptionResponse,
  Ticket,
  TicketCreateResponse,
  TrendingBetResponse,
} from '../data-types/sports/sportsInterfaces';
import { VALIDATION_TYPE } from '../data-types/validation/validationEnums';
import { getOutcomeTranslation } from '../international/translation/outcomes';
import {
  competitionsGet,
  eventGetCommand,
  eventListCommand,
  eventRetrieve,
  eventSearch,
  eventStreamGetCommand,
  eventTrending,
  offeringGetOfferTypeGroups,
  offeringGetOfferTypes,
  offeringGetOutcomes,
  offeringSubscribe,
  offeringUnsubscribe,
  racesGet,
  racesGetFromMeeting,
  racesGetMeetings,
  racesGetNextRaces,
} from '../socket-types/sportSocketTypes';
import { ticketCreateCommand, ticketList } from '../socket-types/ticketSocketTypes';
import { setTrendingBets } from '../state/actions/betActions';
import { setDetailedEvent, setLoadingEvents, setRace } from '../state/actions/sportsbookActions';
import { resetSportsbookEvents } from '../state/actions/sportsbookEventsActions';
import { store } from '../state/store';
import { enqueueSnackbarPrompt } from '../utils/prompts';
import { convertRaceToEvent } from '../utils/sports';
import { setSportsbookEventsInRedux } from '../utils/sportsbook';
import { isOfType } from '../validation/typeValidation';
import { sendCommand } from './commandsHandler';

interface TicketCreate {
  eventId: number;
  sportId: number;
  offerId: number;
  outcomeId: number;
  oddsStr: string;
  fractionalOdds: string;
  extraValue: string | undefined;
}
/**
 * Gets the list of meetings corresponding to day offset and sport id
 */
export const getMeetings = async (listId: string, sportId: number) => {
  const dayOffset = listId === 'today' ? 0 : 1;
  const res = await sendCommand<MeetingsResponse>(racesGetMeetings, { dayOffset, sportId });
  if (res instanceof Error) {
    // throw generic error
    throw res;
  }

  const list: SubSubNavItem[] = [];
  if (res.data.meetings) {
    res.data.meetings.forEach(item => {
      const listItem: SubSubNavItem = {
        listId: item.id.toString(), // NOTE: Revisit this
        title: item.name,
        shortName: item.abbrev3Char,
        type: 'eventList',
        sportId: item.sportId,
        countryName: item.country,
        iconUrl: item.flag,
        date: item.date,
        parentLink: listId,
      };
      list.push(listItem);
    });
  }
  return list;
};

export const searchEvents = async (searchTerm: string): Promise<EventItem[]> => {
  const res = await sendCommand<EventSearchResponse>(eventSearch, { searchTerm });

  if (res instanceof Error) {
    throw new Error(DEFAULT_ERROR_MESSAGE);
  }

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  const list: EventItem[] = [];
  res.data.forEach(event => {
    if ((event as Race).meetingId) {
      const mappedEvent: EventItem = convertRaceToEvent(event as Race);
      list.push(mappedEvent);
    } else {
      list.push(event as EventItem);
    }
  });
  return list;
};

export const eventList = async (payload: EventsListPayloadData): Promise<EventItem[]> => {
  const res = await sendCommand<EventListResponse>(eventListCommand, payload);
  if (res instanceof Error) {
    throw res;
  }

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  const list: EventItem[] = [];
  if (res.data.events) {
    res.data.events.forEach(event => {
      if ((event as Race).meetingId) {
        const mappedEvent: EventItem = convertRaceToEvent(event as Race);
        list.push(mappedEvent);
      } else {
        list.push(event as EventItem);
      }
    });
  }
  return list;
};

export const getNext5 = async (sportId: number): Promise<EventItem[]> => {
  const res = await sendCommand<RaceListResponse>(racesGetNextRaces, { sportId, N: 5 });
  if (res instanceof Error) {
    throw res;
  }

  const list: EventItem[] = [];
  if (res.data && res.data.races) {
    res.data.races.forEach(race => {
      const event: EventItem = convertRaceToEvent(race);
      list.push(event);
    });
  }
  return list;
};

export const getFromMeeting = async (meetingId: number | string): Promise<EventItem[]> => {
  const res = await sendCommand<RaceListResponse>(racesGetFromMeeting, { meetingId });
  if (res instanceof Error) {
    throw res;
  }

  const list: EventItem[] = [];
  if (res.data && res.data.races) {
    res.data.races.forEach(race => {
      const event: EventItem = convertRaceToEvent(race);
      list.push(event);
    });
  }
  return list;
};

export const getRace = async (raceId: number): Promise<RaceDetails> => {
  const res = await sendCommand<RaceGetResponse>(racesGet, { id: raceId });

  if (res.error) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  return res.data;
};

export const ticketCreate = async (
  systemBetTypeId: number | undefined,
  bets: TicketCreate[],
  amount: number,
  accumulator: boolean | { odds: number; fractional: string },
  isEw: boolean = false,
): Promise<Ticket | CommandResponseError> => {
  const res = await sendCommand<TicketCreateResponse>(ticketCreateCommand, { systemBetTypeId, bets, amount, accumulator, isEw });

  if (res.error) {
    return res.error;
  }

  return res.data.ticket;
};

export const getSportsTickets = async (active: boolean, pageIndex: number): Promise<Ticket[]> => {
  const res = await sendCommand<SportsTicketResponse>(ticketList, { active, pageIndex });

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res.data && res.data.tickets) {
    return res.data.tickets;
  }

  throw new Error('Something went wrong whilst fetching your sports bets.');
};

export const getEvents = async (payload: EventsGetPayloadData): Promise<EventItem[]> => {
  let events: EventItem[] = [];
  const { category, listId, subSection, skipLoadingEvents } = payload;

  if (!skipLoadingEvents) {
    setLoadingEvents(true);
  }

  try {
    if (isOfType(category, VALIDATION_TYPE.NUMBER) && Number(category) === sportCategories.HORSES) {
      if (listId === HORSE_RACE_LIST_ID.NEXT_N) {
        events = await getNext5(sportCategories.HORSES);
      } else {
        if (subSection) {
          events = await getFromMeeting(subSection);
        } else {
          events = await getFromMeeting(listId);
        }
      }
    } else {
      const {
        userData: { userLocation },
      } = store.getState();

      events = await eventList({
        listId,
        sportId: isOfType(category, VALIDATION_TYPE.NUMBER) ? Number(category) : 0,
        country: userLocation || AcceptedCountries.GREAT_BRITAIN,
      });
    }
    // TODO: Error handling! Sometimes events.error is a thing
    setLoadingEvents(false);
    return events;
  } catch (error) {
    // TODO: This should just throw the error. Whoever uses this should handle it from there
    enqueueSnackbarPrompt(error.message, { closeOnClick: true });
  }

  setLoadingEvents(false);
  return events;
};

export const updateEvents = async (category: string | number, listId: string, subSection?: string): Promise<void> => {
  resetSportsbookEvents();

  const events = await getEvents({ category, listId, subSection });
  setSportsbookEventsInRedux(events);
};

/**
 * Retrieves the event details from the server. In the background, it also does:
 * - unsubscription for all subscribed events for the user (on the server side)
 * - a subscription for the specified events (on the server side)
 * @param events the list of events whose details to retrieve
 */
export const retrieveEvents = async (events: RetrieveEventRequest[]): Promise<void> => {
  resetSportsbookEvents();

  try {
    const res = await sendCommand<RetrieveEventsResponse>(eventRetrieve, { events });

    if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
      const errorMessage = res.error.notification.userFriendlyMessage;
      throw new Error(errorMessage);
    } else if (res.data) {
      setSportsbookEventsInRedux(res.data.events);
    } else {
      throw new Error();
    }
  } catch (error) {
    throw new Error(error.message || 'Something went wrong while retrieving the events.');
  }
};

export const fetchHorses = async (subsection: string) => {
  resetSportsbookEvents();

  const result = await getFromMeeting(subsection);
  if (result) {
    setSportsbookEventsInRedux(result);
  }
};

export const updateEventsForMultiSportsLists = async (section: string): Promise<void> => {
  resetSportsbookEvents();

  const events = await getEvents({ category: 0, listId: section });
  const eventsWithSection = events.map(event => ({ ...event, forSection: section }));

  setSportsbookEventsInRedux(eventsWithSection);
};

export const subscribeToEvent = async (sportId: number, eventId: number) => {
  setRace(undefined);
  if (sportId !== sportCategories.HORSES) {
    const detailedEvent = await getEventData(eventId);
    setDetailedEvent(detailedEvent);
  } else {
    // for horses, subscription is not necessary (and shouldn't be done, as it silently fails)
    const race = await getRace(eventId);
    setRace(race);
    setDetailedEvent(convertRaceToEvent(race));
  }
};

export const getEvent = async (sportId: number, eventId: number): Promise<EventItem | undefined> => {
  if (sportId !== sportCategories.HORSES) {
    return await getEventData(eventId);
  }

  // for horses, subscription is not necessary (and shouldn't be done, as it silently fails)
  const race = await getRace(eventId);
  return convertRaceToEvent(race);
};

export const fetchTrendingBets = async (): Promise<void> => {
  let errorMessage;

  try {
    const res = await sendCommand<TrendingBetResponse>(eventTrending, {});

    if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
      errorMessage = res.error.notification.userFriendlyMessage;
    } else if (res.data) {
      const trendingBets = res.data.betsList;
      setTrendingBets(trendingBets);
    } else {
      throw new Error('Something went wrong while retrieving the trending events.');
    }
  } catch (error) {
    enqueueSnackbarPrompt(errorMessage || 'Something went wrong while retrieving the trending events.', { closeOnClick: true });
  }
};

export const getDropdownMarkets = async (sportId: number) => {
  try {
    const newSportId = sportIdsLegacyToNew[sportId];

    if (newSportId) {
      const res = await sendCommand<DropdownMarketsResponse>(offeringGetOfferTypes, { sportId: newSportId });

      if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
        throw new Error(res.error.notification.userFriendlyMessage);
      }

      if (res.data) {
        return res.data.offerTypes;
      }

      throw new Error('Something went wrong while retrieving the events.');
    }

    return [];
  } catch (error) {
    throw new Error(error.message || 'Something went wrong while retrieving the events.');
  }
};

const getDropdownMarketObjectByName = (marketName: string, markets: DropdownMarket[]) => {
  for (const market of markets) {
    if (market.name === marketName) {
      return market;
    }
  }

  return undefined;
};

export const getOutcomesForSelectedDropdownMarket = (marketName: string, markets: DropdownMarket[], intl: IntlShape) => {
  const market = getDropdownMarketObjectByName(marketName, markets);

  if (market) {
    return market.outcomeTypes.map(outcome => getOutcomeTranslation(outcome.name, intl).toUpperCase());
  }
  return [];
};

export const getOutcomesForMarket = async (eventId: number, offerTypeId: number) => {
  try {
    const res = await sendCommand<MarketOutcomesResponse>(offeringGetOutcomes, { eventId, offerTypeId });

    if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
      throw new Error(res.error.notification.userFriendlyMessage);
    }

    if (res.data) {
      const outcomes = res.data.outcomes || [];
      const sanitizedOutcomes = outcomes.map(outcome => ({
        ewOdds: outcome.ewOdds,
        fractionalOdds: outcome.fractionalOdds,
        id: outcome.id,
        name: outcome.name,
        odds: outcome.odds,
        offerId: outcome.offerId,
        result: outcome.result,
        status: outcome.status,
        specifier: outcome.specifier,
        extraValue: outcome.extraValue,
      }));

      return sanitizedOutcomes;
    }

    throw new Error('Something went wrong while retrieving the events.');
  } catch (error) {
    throw new Error(error.message || 'Something went wrong while retrieving the events.');
  }
};

export const getEventStreamData = async (eventId: number) => {
  try {
    const res = await sendCommand<EventStreamGetResponse>(eventStreamGetCommand, { eventId });

    if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
      throw new Error(res.error.notification.userFriendlyMessage);
    }

    if (res.data && res.data.payload) {
      return res.data.payload;
    }

    throw new Error('Something went wrong while retrieving the stream.');
  } catch (error) {
    throw new Error(error.message || 'Something went wrong while retrieving the stream.');
  }
};

export const getMarketOfferTypeGroups = async (sportId: number) => {
  try {
    const res = await sendCommand<MarketOfferGroupResponse>(offeringGetOfferTypeGroups, { sportId });

    if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
      throw new Error(res.error.notification.userFriendlyMessage);
    }

    if (res.data) {
      return res.data.offerTypeGroups;
    }

    throw new Error('Something went wrong while retrieving the events.');
  } catch (error) {
    throw new Error(error.message || 'Something went wrong while retrieving the events.');
  }
};

export const getEventData = async (eventId: number): Promise<EventItem | undefined> => {
  try {
    const res = await sendCommand<EventGetResponse>(eventGetCommand, { eventId });

    if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
      throw new Error(res.error.notification.userFriendlyMessage);
    }

    if (res.data) {
      return res.data.result;
    }

    throw new Error('Something went wrong while retrieving the events.');
  } catch (error) {
    throw new Error(error.message || 'Something went wrong while retrieving the events.');
  }
};

export const unsubscribeOffering = async (offerIds: number[], outcomeIds: number[]): Promise<SubscriptionResponse | void> => {
  try {
    await sendCommand<SubscriptionResponse>(offeringUnsubscribe, {
      unsubscribe: [
        { type: 'OFFER', ids: offerIds },
        { type: 'OUTCOME', ids: outcomeIds },
      ],
    });
  } catch (error) {
    // do nothing
  }
};

export const subscribeOffering = async (offerIds: number[], outcomeIds: number[]): Promise<SubscriptionResponse | void> => {
  try {
    await sendCommand<SubscriptionResponse>(offeringSubscribe, {
      subscribe: [
        { type: 'OFFER', ids: offerIds },
        { type: 'OUTCOME', ids: outcomeIds },
      ],
    });
  } catch (error) {
    // do nothing
  }
};

export const resubscribeToDetailedEvent = async () => {
  const pathname = window.location.pathname;
  if (pathname.includes('sport')) {
    const pathParams = pathname.split('/');
    if (pathParams.length === 5) {
      const sportIdForDetailedView = Number(pathParams[2]);
      const eventId = Number(pathParams[4]);
      if (!isNaN(sportIdForDetailedView) && !isNaN(eventId)) {
        try {
          await subscribeToEvent(sportIdForDetailedView, eventId);
        } catch (e) {
          // do nothing
        }
      }
    }
  }
};

export const getCompetitions = async (sportId: number, listId: string, doNotSendListId = false) => {
  const {
    userData: { user, userLocation },
    customization: {
      featureFlags: { bespokeCompetitions },
    },
  } = store.getState();
  const userId = bespokeCompetitions && user && user.id ? user.id : undefined;

  const res = await sendCommand<RacingCompetitionResponse>(competitionsGet, {
    userId,
    sportId,
    listId: doNotSendListId ? undefined : listId,
    country: userLocation || AcceptedCountries.GREAT_BRITAIN,
  });

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  const list: SubSubNavItem[] = [];
  if (res.data.competitions) {
    res.data.competitions.forEach(item => {
      const listItem: SubSubNavItem = {
        listId: `competition-${item.id}`,
        title: item.name,
        shortName: '',
        type: 'eventList',
        sportId: item.sportId,
        countryName: '',
        iconUrl: item.iconUrl,
        date: '',
        parentLink: listId,
        isBespoke: item.isBespoke || false,
        competitionGroupId: item.competitionGroupId || undefined,
      };
      list.push(listItem);
    });
  }
  return list;
};
